import { Tooltip } from "antd";
import { notification, Select } from "antd";
import SVG from "components/common/SVG";
import {
  clientRoles,
  clientRolesForWorkspaceAdmin,
  ENTITY_TYPE,
  ROLE,
  roleLabels,
  userRoleType,
} from "constants/phase";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";

import { assignOrganizationUser, getOrganizationAssignableUsers } from "../../../../store/organization/duck";
import { assignWorkspaceUser, getWorkspaceAssignableUsers } from "../../../../store/workspace/duck";
import CustomModal from "../CustomModal";
import styles from "./style.module.scss";
const mapDispatchToProps = {
  getOrganizationAssignableUsers,
  assignOrganizationUser,
  assignWorkspaceUser,
  getWorkspaceAssignableUsers,
};

const AssignUserModal = ({
  entity,
  entityType,
  setShowModal,
  getOrganizationAssignableUsers,
  assignOrganizationUser,
  closeAndFetchModal,
  assignWorkspaceUser,
  getWorkspaceAssignableUsers,
  isAdmin,
}) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignableUsers, setAssignedUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const { currentRole } = useOutletContext();
  const assignableRoles = useMemo(() => {
    const user = assignableUsers.find((user) => user.value === selectedUser);
    if (entityType === ENTITY_TYPE.WORKSPACES && isAdmin) {
      return Object.values(clientRolesForWorkspaceAdmin).map((role) => ({ value: role, label: roleLabels[role] }));
    }

    if (user?.roleType === userRoleType.generalOps || user?.roleType === userRoleType.privilegeOps) {
      return [{ value: user?.roleType, label: roleLabels[user?.roleType] }];
    } else if (user?.roleType === userRoleType.client && currentRole == ROLE.ADMIN) {
      return Object.values(clientRoles)
        .filter((data) => data !== ROLE.ADMIN)
        .map((role) => ({ value: role, label: roleLabels[role] }));
    }
    if (user?.roleType === userRoleType.client) {
      return Object.values(clientRoles).map((role) => ({ value: role, label: roleLabels[role] }));
    }

    // return [];
  }, [assignableUsers, selectedUser]);

  const handleAssignUser = async () => {
    try {
      setLoading(true);

      if (entityType === ENTITY_TYPE.ORGANIZATION) {
        const payload = {
          userId: selectedUser,
          role: selectedRole,
          organizationId: entity?.organizationId,
        };

        const response = await assignOrganizationUser(payload);
        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } else {
        const payload = {
          userId: selectedUser,
          role: selectedRole,
          workspaceId: entity?.workspaceId,
        };

        const response = await assignWorkspaceUser(payload);
        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      }

      closeAndFetchModal();
    } catch (error) {
      console.log(error);
      if (error.message) {
        notification.error({ message: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableUsers = useCallback(
    async (entity) => {
      try {
        if (entityType === ENTITY_TYPE.ORGANIZATION) {
          const response = await getOrganizationAssignableUsers({ organizationId: entity?.organizationId });

          if (response?.value?.users) {
            setAssignedUsers(
              response.value.users.map(({ name, userId, roleType }) => ({ value: userId, label: name, roleType })),
            );
          }
        } else {
          const response = await getWorkspaceAssignableUsers({ workspaceId: entity?.workspaceId });

          if (response?.value?.users) {
            setAssignedUsers(
              response.value.users.map(({ name, userId, roleType }) => ({ value: userId, label: name, roleType })),
            );
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [entityType, getOrganizationAssignableUsers, getWorkspaceAssignableUsers],
  );

  useEffect(() => {
    fetchAssignableUsers(entity);
  }, [entity, fetchAssignableUsers]);

  return (
    <>
      <CustomModal
        showModal={true}
        setShowModal={setShowModal}
        contentLabel="Assign User"
        primaryCtaText="Add"
        secondaryCtaText="Close"
        primaryCtaHandler={handleAssignUser}
        secondaryCtaHandler={() => setShowModal(false)}
        primaryCtaEnabled={!loading && selectedRole && selectedUser}
      >
        <div className={styles.formContainer}>
          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>
              {entityType === ENTITY_TYPE.ORGANIZATION ? "Organization" : "Workspace"}
            </div>
            <div className={styles.organizationInfoContainer}>
              {entity?.logo && (
                <div className={styles.logo}>
                  <img className={styles.logoPreview} src={entity?.logo} alt="profile-preview" />
                </div>
              )}

              <div className={styles.name}>{entity?.name}</div>
            </div>
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Select User</div>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              className="custom-ant-select-v2"
              onChange={(user) => {
                setSelectedUser(user);
                setSelectedRole(null);
              }}
              options={assignableUsers}
            />
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Select Role</div>
            <div className={styles.workspaceSelectContainer}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                className="custom-ant-select-v2"
                disabled={!selectedUser}
                value={selectedRole}
                onChange={(role) => setSelectedRole(role)}
                options={assignableRoles}
              />

              <Tooltip placement="top" title={"Ops roles are not editable"} arrow>
                <SVG className={styles.infoIcon} iconName={"images/info.svg"} />
              </Tooltip>
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default connect((/*state*/) => ({}), mapDispatchToProps)(AssignUserModal);
